import React, { useEffect, useRef, useState } from "react";
import { KeenIcon, Scrollspy } from "@/components";
import { Modal, ModalContent, ModalBody, ModalHeader } from "@/components/modal";
import { useResponsive, useViewport } from "@/hooks";
import { Intro } from "@/pages/buddy/company/blocks/source/Intro";
import { UploadDocument } from "@/pages/buddy/shared/new-soruce/UploadDocument";
import { UploadText } from "@/pages/buddy/shared/new-soruce/UploadText";
import { CrawlWebpage } from "@/pages/buddy/shared/new-soruce/CrawlWebpage";
import { CrawlLinkedIn } from "@/pages/buddy/shared/new-soruce/CrawlLinkedIn";
import { CompanyDetailsData } from "@/pages/buddy/types/CompanyDetailsData";
import {SidebarModalMenu} from "@/pages/buddy/shared/new-soruce/SidebarModelMenu.tsx";

interface CompanyModalProps {
    open: boolean;
    onClose: () => void;
    mode: "create" | "addSource";
    company?: CompanyDetailsData | null;
    refreshCompanyData?: () => void;
}

const CompanyModal: React.FC<CompanyModalProps> = ({
                                                       open,
                                                       onClose,
                                                       mode,
                                                       company = null,
                                                       refreshCompanyData,
                                                   }) => {
    const isCreateMode = mode === "create";
    const isAddSourceMode = mode === "addSource";

    const desktopMode = useResponsive("up", "lg");
    const navBar = useRef<any | null>(null);
    const parentRef = useRef<any | null>(null);
    const [sidebarHeight, setSidebarHeight] = useState<number>(0);
    const [viewportHeight] = useViewport();
    const offset = 260;

    useEffect(() => {
        setSidebarHeight(viewportHeight - offset);
    }, [viewportHeight]);

    const getTitle = (): string => {
        if (isCreateMode) {
            return "Add New Company";
        }
        if (isAddSourceMode && company) {
            return `Add New Source to Company ${company.name}`;
        }
        return "Company";
    };

    // For companies (a top-level entity), no parent selection is needed.
    const entity = company ? { p_id: company.p_id, name: company.name } : null;

    return (
        <Modal open={open} onClose={onClose} className="!flex">
            <ModalContent className="container-fixed px-30 overflow-hidden pt-7.5 my-[3%] w-full">
                <ModalHeader className="p-0 border-0">
                    <div className="flex items-center justify-between flex-wrap grow gap-5 pb-7.5">
                        <div className="flex items-baseline space-x-2">
                            <KeenIcon icon="home" className="text-3xl" />
                            <h1 className="text-xl font-semibold leading-none text-gray-900">{getTitle()}</h1>
                        </div>
                        <div className="flex items-center gap-3">
                            <button className="btn btn-sm btn-light" onClick={onClose}>
                                Close
                            </button>
                        </div>
                    </div>
                </ModalHeader>
                <ModalBody className="scrollable-y py-0 mb-5 ps-0 pe-3 -me-5.5" ref={parentRef}>
                    <div className="flex grow gap-5 lg:gap-7.5">
                        {desktopMode && (
                            <div className="block w-[230px] shrink-0">
                                <div
                                    ref={navBar}
                                    className="w-[230px] fixed z-10 scrollable-y-auto"
                                    style={{ maxHeight: `${sidebarHeight}px` }}
                                >
                                    <Scrollspy offset={100} targetRef={parentRef}>
                                        <SidebarModalMenu />
                                    </Scrollspy>
                                </div>
                            </div>
                        )}
                        <div className="flex flex-col items-stretch grow gap-5 lg:gap-7.5">
                            <Intro mode={mode} />
                            <UploadDocument
                                parentEntity={entity}
                                objectEntity={entity}
                                entityType="company"
                                mode={mode}
                                onSuccess={refreshCompanyData}
                            />
                            <UploadText
                                parentEntity={entity}
                                objectEntity={entity}
                                entityType="company"
                                mode={mode}
                                onSuccess={refreshCompanyData}
                            />
                            <CrawlWebpage
                                parentEntity={entity}
                                objectEntity={entity}
                                entityType="company"
                                mode={mode}
                                onSuccess={refreshCompanyData}
                            />
                            <CrawlLinkedIn />
                        </div>
                    </div>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export { CompanyModal };
